import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { parse, stringify } from '../../util/urlHelpers';
import { injectIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { getSkillsAndToolsSuggestions, searchProfileListings } from './BrowseFreelancersPage.duck';
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { updateFavoriteProfiles } from '../../ducks/user.duck';

import {
  Footer,
  Page,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
} from '../../components';
import { TopbarContainer } from '../../containers';
import BrowseFreelancersSearchBar from './BrowseFreelancersSearchBar';
import BrowseFreelancersFilters from './BrowseFreelancersFilters';
import BrowseFreelancersResults from './BrowseFreelancersResults';
import config from '../../config';

import css from './BrowseFreelancersPage.css';

const BrowseFreelancersPageComponent = props => {
  const [currentFilters, setCurrentFilters] = useState([]);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [clearSearchBar, setClearSearchBar] = useState(false);
  const isUpdatingURL = useRef(false);

  const {
    scrollingDisabled,
    history,
    intl,
    onGetSkillsAndToolsSuggestions,
    currentUser,
    pageResults,
    pagination,
    currentPageReviews,
    otherListings,
    skillsAndToolsSuggestions,
    skillOptions,
    toolOptions,
    favoriteProfileListingsIds,
    favoriteProfilesIds,
    onUpdateFavoriteProfiles,
    offPlatformTestimonials,
    queryOffPlatformTestimonialsError,
    offPlatformReviews,
    queryOffPlatformReviewsError,
  } = props;

  const schemaTitle = intl.formatMessage({ id: 'BrowseFreelancersPage.title' });
  const schemaDescription = intl.formatMessage({ id: 'BrowseFreelancersPage.schemaDescription' });
  const pageUrl = `${config.canonicalRootURL}${props.location.pathname}`;

  const freelancerItems = (pageResults || [])
    .filter(result => result?.author?.id)
    .map((result, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${config.canonicalRootURL}/u/${result.author.id.uuid}`,
    }));

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: schemaTitle,
    description: schemaDescription,
    url: pageUrl,
    ...(freelancerItems.length > 0 && {
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: freelancerItems.length,
        itemListElement: freelancerItems,
      },
    }),
  };

  // Helper function to get filter label by field name and value
  const getFilterLabel = useCallback(
    (fieldName, value, options = {}) => {
      switch (fieldName) {
        case 'skills':
          const skill = skillOptions?.find(option => option.key === value);
          if (skill && skill.label) {
            // Check if label is from API (string) or FormattedMessage component
            if (typeof skill.label === 'string') {
              return skill.label;
            } else {
              // For FormattedMessage components, use intl.formatMessage with the id
              return intl.formatMessage({ id: skill.label.props.id });
            }
          }
          return value;
        case 'tools':
          const tool = toolOptions?.find(option => option.key === value);
          if (tool && tool.label) {
            // Check if label is from API (string) or FormattedMessage component
            if (typeof tool.label === 'string') {
              return tool.label;
            } else {
              // For FormattedMessage components, use intl.formatMessage with the id
              return intl.formatMessage({ id: tool.label.props.id });
            }
          }
          return value;
        case 'language':
          const filters = config.custom.filters;
          const languages = filters.find(filter => filter.id === 'languages').config.options;
          const language = languages.find(lang => lang.key === value);
          return language ? intl.formatMessage({ id: language.label.props.id }) : value;
        case 'hobbies':
          const hobbyOptions = config.custom.hobbyOptions;
          const hobby = hobbyOptions?.find(option => option.key === value);
          return hobby ? intl.formatMessage({ id: hobby.label.props.id }) : value;
        case 'hourlyRate':
          const hourlyRateLabels = {
            '20-39': '20-39 €',
            '40-59': '40-59 €',
            '60-79': '60-79 €',
            '80-99': '80-99 €',
            '100+': '+ 100 €',
          };
          return hourlyRateLabels[value] || value;
        default:
          return value;
      }
    },
    [skillOptions, toolOptions, intl]
  );

  // Initialize filters from URL parameters
  const initializeFiltersFromURL = useCallback(() => {
    const urlParams = parse(props.location.search);
    const filters = [];

    // Skip creating filters for suggestion selections (when type parameter is present)
    // These should only affect the search query, not display as filter chips

    // Handle skills
    if (urlParams.skills) {
      const skillKeys = urlParams.skills.split(',');
      skillKeys.forEach(key => {
        const skill = skillOptions?.find(option => option.key === key);
        if (skill) {
          filters.push({
            id: `skills-${key}`,
            label: getFilterLabel('skills', key),
            field: 'skills',
            value: skill,
          });
        }
      });
    }

    // Handle tools
    if (urlParams.tools) {
      const toolKeys = urlParams.tools.split(',');
      toolKeys.forEach(key => {
        const tool = toolOptions?.find(option => option.key === key);
        if (tool) {
          filters.push({
            id: `tools-${key}`,
            label: getFilterLabel('tools', key),
            field: 'tools',
            value: tool,
          });
        }
      });
    }

    // Handle language
    if (urlParams.language) {
      const languageKeys = urlParams.language.split(',');
      languageKeys.forEach(key => {
        filters.push({
          id: `language-${key}`,
          label: getFilterLabel('language', key),
          field: 'language',
          value: { key },
        });
      });
    }

    // Handle hobbies
    if (urlParams.hobbies) {
      const hobbyKeys = urlParams.hobbies.split(',');
      hobbyKeys.forEach(key => {
        filters.push({
          id: `hobbies-${key}`,
          label: getFilterLabel('hobbies', key),
          field: 'hobbies',
          value: { key },
        });
      });
    }

    // Handle hourlyRate
    if (urlParams.hourlyRate) {
      const hourlyRateValues = urlParams.hourlyRate.split(',');
      hourlyRateValues.forEach(value => {
        filters.push({
          id: `hourlyRate-${value}`,
          label: getFilterLabel('hourlyRate', value),
          field: 'hourlyRate',
          value: value,
        });
      });
    }

    return filters;
  }, [props.location.search, skillOptions, toolOptions, getFilterLabel]);

  // Helper function to check if user had selected item from suggestions
  const hasSelectedItemFromSuggestions = () => {
    const urlParams = parse(props.location.search);
    // If type parameter is present, it means user selected from suggestions
    return !!urlParams.type;
  };

  const onSetKeywords = keywords => {
    // Prevent duplicate calls during URL updates
    if (isUpdatingURL.current) {
      return;
    }

    // Clear all filters and reviews when doing a new keyword search
    setCurrentFilters([]);

    isUpdatingURL.current = true;
    const queryParams = { keywords };
    const newSearch = stringify(queryParams);
    props.history.push({ search: newSearch });

    // Reset the flag after a delay to allow URL change effect to handle the API call
    setTimeout(() => {
      isUpdatingURL.current = false;
    }, 50);
  };

  const onClearAllFilters = () => {
    // Check if user had selected item from suggestions
    const hadSuggestionSelection = hasSelectedItemFromSuggestions();

    isUpdatingURL.current = true;

    setCurrentFilters([]);

    const existingParams = parse(props.location.search);

    const cleanParams = {};
    if (existingParams.keywords && !hadSuggestionSelection) {
      // Only preserve keywords if they weren't from a suggestion selection
      cleanParams.keywords = existingParams.keywords;
    }

    const newSearch = stringify(cleanParams);

    props.history.push({ search: newSearch });

    // Clear search bar if user had selected from suggestions
    if (hadSuggestionSelection) {
      setClearSearchBar(true);
      // Reset the clear flag after triggering
      setTimeout(() => setClearSearchBar(false), 100);
    }

    isUpdatingURL.current = false;
  };

  const onSelectSkillsAndToolsSuggestion = suggestion => {
    // Prevent duplicate calls during URL updates
    if (isUpdatingURL.current) {
      return;
    }

    setCurrentFilters([]);

    isUpdatingURL.current = true;
    const queryParams = {};

    // Add suggestion text to keywords parameter and type parameter
    queryParams.keywords = suggestion.key;
    queryParams.type = suggestion.type;

    const newSearch = stringify(queryParams);
    props.history.push({ search: newSearch });

    // Reset the flag after a delay to allow URL change effect to handle the API call
    setTimeout(() => {
      isUpdatingURL.current = false;
    }, 50);
  };

  const constructParamsFromCurrentFilters = updatedFilters => {
    if (isUpdatingURL.current) {
      return; // Prevent recursive URL updates
    }

    isUpdatingURL.current = true;

    // Use provided filters or current filters
    const filtersToUse = updatedFilters || currentFilters;

    // Start with existing search parameters to preserve keywords and other params
    const existingParams = parse(props.location.search);
    const params = { ...existingParams };

    // Reset page to 1 when filters change
    delete params.page;

    // Remove all filter-related params first
    delete params.skills;
    delete params.tools;
    delete params.language;
    delete params.hobbies;
    delete params.hourlyRate;

    // Group filters by field type to handle multiple values
    const filtersByField = filtersToUse.reduce((acc, filter) => {
      if (!acc[filter.field]) {
        acc[filter.field] = [];
      }
      acc[filter.field].push(filter.value);
      return acc;
    }, {});

    // Handle each filter type appropriately
    Object.keys(filtersByField).forEach(field => {
      const values = filtersByField[field];

      if (field === 'skills' || field === 'tools') {
        // For skills and tools, send as comma-separated keys
        params[field] = values.map(v => v.key).join(',');
      } else if (field === 'language') {
        // For language, send as comma-separated keys
        params.language = values.map(v => v.key).join(',');
      } else if (field === 'hobbies') {
        // For hobbies, send as comma-separated keys
        params.hobbies = values.map(v => v.key).join(',');
      } else if (field === 'hourlyRate') {
        // For hourlyRate, send as comma-separated values
        params.hourlyRate = values.join(',');
      }
    });

    const newSearch = stringify(params);
    props.history.push({ search: newSearch });

    isUpdatingURL.current = false;
  };

  // Initialize filters from URL when options are loaded
  useEffect(() => {
    if (!filtersInitialized && skillOptions.length > 0 && toolOptions.length > 0) {
      const initialFilters = initializeFiltersFromURL();
      setCurrentFilters(initialFilters);
      setFiltersInitialized(true);
    }
  }, [skillOptions, toolOptions, filtersInitialized, initializeFiltersFromURL]);

  // Handle browser back/forward navigation and URL changes
  useEffect(() => {
    // Only handle URL changes after initial load, and not during programmatic updates
    if (filtersInitialized && !isUpdatingURL.current) {
      const newFilters = initializeFiltersFromURL();
      setCurrentFilters(newFilters);
    }
  }, [props.location.search, filtersInitialized, initializeFiltersFromURL]);

  const handleAddRemoveFavorite = (isFavoriteProfile, currentProfileId) => {
    if (!currentUser) {
      history.push('/signup');
      return;
    } else {
      if (isFavoriteProfile) {
        const newFavoriteProfilesIds = favoriteProfilesIds.filter(id => id !== currentProfileId);
        onUpdateFavoriteProfiles(newFavoriteProfilesIds);
      } else {
        onUpdateFavoriteProfiles([...favoriteProfilesIds, currentProfileId]);
      }
    }
  };

  // Helper function to get the display value for keywords in search bar
  const getInitialKeywordsDisplay = () => {
    const urlParams = parse(props.location.search);

    // If we have a stored display value (from suggestion selection with added filters), use it
    if (urlParams.keywordsDisplay && urlParams.keywords) {
      return urlParams.keywordsDisplay;
    }

    // If it's a suggestion selection (type parameter present), show the translated text
    if (urlParams.type && urlParams.keywords) {
      if (urlParams.type === 'skill') {
        const skill = skillOptions?.find(option => option.key === urlParams.keywords);
        return skill?.label && typeof skill.label === 'string'
          ? skill.label
          : skill?.label?.props?.id
          ? intl.formatMessage({ id: skill.label.props.id })
          : urlParams.keywords;
      } else if (urlParams.type === 'tool') {
        const tool = toolOptions?.find(option => option.key === urlParams.keywords);
        return tool?.label && typeof tool.label === 'string'
          ? tool.label
          : tool?.label?.props?.id
          ? intl.formatMessage({ id: tool.label.props.id })
          : urlParams.keywords;
      }
    }

    // For regular keyword searches, return the keywords as-is
    return urlParams.keywords || '';
  };

  const urlParams = parse(props.location.search);

  const resultsForText = (
    <FormattedMessage
      id="BrowseFreelancersSearchBar.resultsFor"
      values={{ searchTerm: urlParams.keywords || '', b: chunks => <b>{chunks}</b> }}
    />
  );
  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle + ' | Freedomly.io'}
      description={schemaDescription}
      schema={pageSchema}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="BrowseFreelancersPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain className={css.main}>
          <BrowseFreelancersSearchBar
            onGetSkillsAndToolsSuggestions={onGetSkillsAndToolsSuggestions}
            skillsAndToolsSuggestions={skillsAndToolsSuggestions}
            onSetKeywords={onSetKeywords}
            onSelectSkillsAndToolsSuggestion={onSelectSkillsAndToolsSuggestion}
            currentFilters={currentFilters}
            setCurrentFilters={setCurrentFilters}
            initialKeywords={getInitialKeywordsDisplay()}
            clearSearchBar={clearSearchBar}
          />
          {urlParams.keywords ? <div className={css.resultsFor}>{resultsForText}</div> : null}
          <div className={css.content}>
            <BrowseFreelancersFilters
              currentFilters={currentFilters}
              setCurrentFilters={setCurrentFilters}
              skillOptions={skillOptions}
              toolOptions={toolOptions}
              onClearAllFilters={onClearAllFilters}
              constructParamsFromCurrentFilters={constructParamsFromCurrentFilters}
            />
            <BrowseFreelancersResults
              pageResults={pageResults}
              currentPageReviews={currentPageReviews}
              offPlatformTestimonials={offPlatformTestimonials}
              queryOffPlatformTestimonialsError={queryOffPlatformTestimonialsError}
              offPlatformReviews={offPlatformReviews}
              queryOffPlatformReviewsError={queryOffPlatformReviewsError}
              skillOptions={skillOptions}
              toolOptions={toolOptions}
              pagination={pagination}
              search={parse(props.location.search)}
              favoriteProfileListingsIds={favoriteProfileListingsIds}
              handleAddRemoveFavorite={handleAddRemoveFavorite}
              otherListings={otherListings}
              currentUser={currentUser}
            />
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool, object } = PropTypes;

BrowseFreelancersPageComponent.propTypes = {
  intl: intlShape.isRequired,
  scrollingDisabled: bool.isRequired,
  location: object.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;

  const {
    pagination,
    searchParams,
    searchInProgress,
    searchTalentError,
    currentPageResultIds,
    currentPageReviews,
    getReviewsInProgress,
    queryReviewsError,
    otherListingRefs,
    queryOtherListingsError,
    skillsAndToolsSuggestions,
    getSkillsAndToolsSuggestionsInProgress,
    getSkillsAndToolsSuggestionsError,
    offPlatformTestimonials,
    queryOffPlatformTestimonialsError,
    offPlatformReviews,
    queryOffPlatformReviewsError,
  } = state.BrowseFreelancersPage;

  const { skillOptions, toolOptions } = state.SkillsAndTools;

  const pageResults = getListingsById(state, currentPageResultIds);

  const otherListings = getMarketplaceEntities(state, otherListingRefs);

  return {
    currentUser,
    pageResults,
    pagination,
    searchParams,
    searchInProgress,
    searchTalentError,
    currentPageResultIds,
    currentPageReviews,
    getReviewsInProgress,
    queryReviewsError,
    otherListings,
    queryOtherListingsError,
    skillsAndToolsSuggestions,
    getSkillsAndToolsSuggestionsInProgress,
    getSkillsAndToolsSuggestionsError,
    offPlatformTestimonials,
    queryOffPlatformTestimonialsError,
    offPlatformReviews,
    queryOffPlatformReviewsError,
    skillOptions,
    toolOptions,
    favoriteProfileListingsIds: state.user.favoriteProfileListingsIds,
    favoriteProfilesIds: state.user.favoriteProfilesIds,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onGetSkillsAndToolsSuggestions: searchTerm => dispatch(getSkillsAndToolsSuggestions(searchTerm)),
  onUpdateFavoriteProfiles: favoriteProfilesUuids =>
    dispatch(updateFavoriteProfiles(favoriteProfilesUuids)),
});

const BrowseFreelancersPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(BrowseFreelancersPageComponent);

BrowseFreelancersPage.loadData = (params, search) => {
  const queryParams = parse(search);
  const keywords = queryParams.keywords || '';
  const skills = queryParams.skills || '';
  const tools = queryParams.tools || '';
  const language = queryParams.language || '';
  const hobbies = queryParams.hobbies || '';
  const hourlyRate = queryParams.hourlyRate || '';
  const type = queryParams.type || '';
  const { page = 1, ...rest } = queryParams;

  const searchParamsForAPI = {
    ...rest,
    page,
    pub_listingType: 'profile',
    meta_profileTier: [50, 500],
    meta_showAtMarketPlace: true,
    perPage: 10,
    include: ['author', 'author.profileImage', 'images'],
    'fields.listing': ['title', 'publicData'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.landscape-crop',
      'variants.landscape-crop2x',
      'variants.horizontal',
      'variants.horizontal-2x',
      'variants.vertical',
      'variants.vertical-2x',
      'variants.square',
      'variants.square2x',
    ],
    'limit.images': 3,
  };

  const userRoles = ['freelancer', 'light-entrepreneur'];
  searchParamsForAPI.pub_authorRole = `has_any:${userRoles.join(',')}`;

  // Track if keywords are being used
  let hasKeywords = false;

  // Handle type parameter - if present, add keywords to the appropriate skills/tools parameter
  if (type && keywords) {
    hasKeywords = true;
    if (type === 'skill') {
      // Add keywords to skills parameter for SDK call
      const existingSkills = skills ? skills.split(',') : [];
      const keywordsToAdd = keywords.split(',');
      const allSkills = [...existingSkills, ...keywordsToAdd].join(',');
      searchParamsForAPI.pub_authorSkillsKeys = `has_all:${allSkills}`;
    } else if (type === 'tool') {
      // Add keywords to tools parameter for SDK call
      const existingTools = tools ? tools.split(',') : [];
      const keywordsToAdd = keywords.split(',');
      const allTools = [...existingTools, ...keywordsToAdd].join(',');
      searchParamsForAPI.pub_authorToolsKeys = `has_all:${allTools}`;
    }
  } else {
    // Original logic for when type parameter is not present
    // Format multi-enum parameters with has_all: prefix for AND semantics
    if (skills) {
      searchParamsForAPI.pub_authorSkillsKeys = `has_all:${skills}`;
    }
    if (tools) {
      searchParamsForAPI.pub_authorToolsKeys = `has_all:${tools}`;
    }

    // Add keywords to the API call for regular keyword search
    if (keywords) {
      hasKeywords = true;
      searchParamsForAPI.keywords = keywords;
    }
  }

  if (language) {
    searchParamsForAPI.pub_authorLanguagesKeys = `has_all:${language}`;
  }
  if (hobbies) {
    searchParamsForAPI.pub_authorHobbiesKeys = `has_all:${hobbies}`;
  }
  if (hourlyRate) {
    searchParamsForAPI.pub_authorHourlyRates = `has_any:${hourlyRate}`;
  }

  // Only add sort by meta_profileTier if no keywords are present
  if (!hasKeywords) {
    searchParamsForAPI.sort = ['meta_profileTier'];
  }

  return searchProfileListings(searchParamsForAPI);
};

export default BrowseFreelancersPage;
